/**
 * IDPoolManager Service - Performance-Optimierung für französische IDs
 * Phase 4: Monitoring und Optimierung
 * 
 * Funktionen:
 * - ID-Pools für schnelle Generierung
 * - Batch-Generierung
 * - Caching-Strategien
 * - Performance-Monitoring
 */

// Import französische ID-Generatoren
import { 
  generateVenteId,
  generateAchatId,
  generateClientId,
  generateFournisseurId,
  generateCommandeId,
  generateDepenseId,
  generateTravailleurId
} from '../utils/frenchIdGenerator';
import isoStandardIdGenerator from '../utils/ISOStandardIdGenerator';

// Lazy import to avoid circular dependency
let idTracker = null;

const getIDTracker = () => {
  if (!idTracker) {
    try {
      idTracker = require('../services/IDTracker').default;
    } catch (error) {
      console.warn('IDTracker not available:', error);
      return null;
    }
  }
  return idTracker;
};

class IDPoolManager {
  constructor() {
    this.pools = new Map();
    this.poolSize = 100; // Vorgenerierte IDs pro Pool
    this.minPoolSize = 20; // Mindestanzahl IDs im Pool
    this.maxPoolSize = 500; // Maximale Pool-Größe
    this.generationStats = {
      totalGenerated: 0,
      poolHits: 0,
      poolMisses: 0,
      averagePoolTime: 0,
      averageGenerationTime: 0
    };
    this.initializePools();
  }

  /**
   * Initialisiert alle ID-Pools
   */
  async initializePools() {
    const types = ['vente', 'achat', 'client', 'fournisseur', 'commande', 'depense', 'travailleur'];
    
    for (const type of types) {
      await this.initializePool(type);
    }
    
    // All pools initialized
  }

  /**
   * Initialisiert einen Pool für einen bestimmten ID-Typ
   * @param {string} type - Der ID-Typ
   */
  async initializePool(type) {
    const startTime = Date.now();
    const pool = [];
    
    try {
      // Pool mit vorgenerierten IDs füllen
      for (let i = 0; i < this.poolSize; i++) {
        const id = await this.generateId(type);
        pool.push({
          id,
          generatedAt: Date.now(),
          used: false
        });
      }
      
      this.pools.set(type, pool);
      
      const generationTime = Date.now() - startTime;
      this.updateStats('pool_generation', generationTime);
      
      // Pool initialized successfully
    } catch (error) {
      console.error(`IDPoolManager: Error initializing pool for ${type}:`, error);
    }
  }

  /**
   * Generiert eine ID für einen bestimmten Typ - ISO-Standard Konform
   * @param {string} type - Der ID-Typ
   * @returns {Promise<string>} Generierte ID
   */
  async generateId(type) {
    const startTime = Date.now();
    
    try {
      // Verwende ISO-Standard Generator für sequenzielle IDs
      let isoId;
      
      switch (type) {
        case 'vente':
          isoId = isoStandardIdGenerator.generateVenteId();
          break;
        case 'achat':
          isoId = isoStandardIdGenerator.generateAchatId();
          break;
        case 'client':
          isoId = isoStandardIdGenerator.generateClientId();
          break;
        case 'fournisseur':
          isoId = isoStandardIdGenerator.generateFournisseurId();
          break;
        case 'commande':
          isoId = isoStandardIdGenerator.generateCommandeId();
          break;
        case 'depense':
          isoId = isoStandardIdGenerator.generateDepenseId();
          break;
        case 'travailleur':
          isoId = isoStandardIdGenerator.generateTravailleurId();
          break;
        default:
          throw new Error(`Unknown ID type: ${type}`);
      }
      
      console.log(`IDPoolManager: Generated ISO-compliant ${type} ID:`, isoId);
      return isoId;
      
    } catch (error) {
      console.error(`IDPoolManager: Error generating ISO ${type} ID:`, error);
      
      // Fallback: Verwende alte Methode
      try {
        switch (type) {
          case 'vente':
            return await generateVenteId(null, { usePool: false });
          case 'achat':
            return generateAchatId(null);
          case 'client':
            return generateClientId(null);
          case 'fournisseur':
            return generateFournisseurId(null);
          case 'commande':
            return generateCommandeId(null);
          case 'depense':
            return generateDepenseId(null);
          case 'travailleur':
            return generateTravailleurId(null);
          default:
            throw new Error(`Unknown ID type: ${type}`);
        }
      } catch (fallbackError) {
        console.error(`IDPoolManager: Fallback also failed for ${type}:`, fallbackError);
        throw fallbackError;
      }
    } finally {
      const generationTime = Date.now() - startTime;
      this.updateStats('id_generation', generationTime);
    }
  }

  /**
   * Generate fallback IDs when business ID is not available
   */
  generateFallbackId(type) {
    const timestamp = Date.now().toString().slice(-5);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    switch (type) {
      case 'vente':
        return `VENTE${timestamp}${random}`;
      case 'achat':
        return `ACHAT${timestamp}${random}`;
      case 'client':
        return `CLIENT${timestamp}${random}`;
      case 'fournisseur':
        return `FOURNISSEUR${timestamp}${random}`;
      case 'commande':
        return `COMMANDE${timestamp}${random}`;
      case 'depense':
        return `DEP${timestamp}${random}`;
      case 'travailleur':
        return `TRAVAILLEUR${timestamp}${random}`;
      default:
        return `${type.toUpperCase()}${timestamp}${random}`;
    }
  }

  /**
   * Holt eine ID aus dem Pool oder generiert eine neue
   * @param {string} type - Der ID-Typ
   * @param {Object} options - Zusätzliche Optionen
   * @returns {Promise<string>} ID aus Pool oder neu generiert
   */
  async getNextId(type, options = {}) {
    const startTime = Date.now();
    
    try {
      // Pool für diesen Typ abrufen
      let pool = this.pools.get(type);
      
      if (!pool) {
        // Pool existiert nicht, erstelle einen neuen
        await this.initializePool(type);
        pool = this.pools.get(type);
      }
      
      // Verfügbare ID im Pool finden
      const availableId = pool.find(item => !item.used);
      
      if (availableId) {
        // ID aus Pool verwenden
        availableId.used = true;
        availableId.usedAt = Date.now();
        
        this.generationStats.poolHits++;
        this.updateStats('pool_hit', Date.now() - startTime);
        
        // Pool bei Bedarf auffüllen
        this.refillPoolIfNeeded(type);
        
        return availableId.id;
      } else {
        // Pool leer, neue ID generieren
        this.generationStats.poolMisses++;
        const newId = await this.generateId(type);
        this.updateStats('pool_miss', Date.now() - startTime);
        
        return newId;
      }
    } catch (error) {
      console.error(`IDPoolManager: Error getting next ID for ${type}:`, error);
      // Fallback: Direkte Generierung
      return await this.generateId(type);
    }
  }

  /**
   * Füllt einen Pool auf, wenn er zu wenig IDs hat
   * @param {string} type - Der ID-Typ
   */
  async refillPoolIfNeeded(type) {
    const pool = this.pools.get(type);
    if (!pool) return;
    
    const availableCount = pool.filter(item => !item.used).length;
    
    if (availableCount < this.minPoolSize) {
      console.log(`🔄 IDPoolManager: Refilling pool for ${type} (${availableCount} available)`);
      
      // Neue IDs generieren und zum Pool hinzufügen
      const newIds = [];
      const refillCount = Math.min(this.poolSize - pool.length, this.maxPoolSize - pool.length);
      
      for (let i = 0; i < refillCount; i++) {
        try {
          const id = await this.generateId(type);
          newIds.push({
            id,
            generatedAt: Date.now(),
            used: false
          });
        } catch (error) {
          console.warn(`IDPoolManager: Error generating ID for refill:`, error);
        }
      }
      
      // Neue IDs zum Pool hinzufügen
      pool.push(...newIds);
      
      console.log(`✅ IDPoolManager: Pool refilled for ${type} (+${newIds.length} IDs)`);
    }
  }

  /**
   * Generiert mehrere IDs auf einmal (Batch-Generierung)
   * @param {string} type - Der ID-Typ
   * @param {number} count - Anzahl der zu generierenden IDs
   * @returns {Promise<Array<string>>} Array von generierten IDs
   */
  async generateBatch(type, count = 10) {
    const startTime = Date.now();
    const ids = [];
    
    try {
      // IDs aus Pool holen
      for (let i = 0; i < count; i++) {
        const id = await this.getNextId(type);
        ids.push(id);
      }
      
      const generationTime = Date.now() - startTime;
      this.updateStats('batch_generation', generationTime);
      
      console.log(`🚀 IDPoolManager: Generated ${count} ${type} IDs in ${generationTime}ms`);
      return ids;
    } catch (error) {
      console.error(`IDPoolManager: Error generating batch for ${type}:`, error);
      return ids;
    }
  }

  /**
   * Aktualisiert die Performance-Statistiken
   * @param {string} operation - Der Operationstyp
   * @param {number} time - Die Ausführungszeit
   */
  updateStats(operation, time) {
    this.generationStats.totalGenerated++;
    
    if (operation === 'pool_hit') {
      const currentAvg = this.generationStats.averagePoolTime;
      const total = this.generationStats.poolHits;
      this.generationStats.averagePoolTime = 
        (currentAvg * (total - 1) + time) / total;
    } else if (operation === 'id_generation') {
      const currentAvg = this.generationStats.averageGenerationTime;
      const total = this.generationStats.totalGenerated;
      this.generationStats.averageGenerationTime = 
        (currentAvg * (total - 1) + time) / total;
    }
  }

  /**
   * Gibt die aktuellen Pool-Statistiken zurück
   * @returns {Object} Pool-Statistiken
   */
  getPoolStats() {
    const stats = {
      pools: {},
      overall: {
        totalPools: this.pools.size,
        totalGenerated: this.generationStats.totalGenerated,
        poolHits: this.generationStats.poolHits,
        poolMisses: this.generationStats.poolMisses,
        hitRate: this.generationStats.totalGenerated > 0 ? 
          (this.generationStats.poolHits / this.generationStats.totalGenerated * 100).toFixed(2) : 0,
        averagePoolTime: this.generationStats.averagePoolTime.toFixed(2),
        averageGenerationTime: this.generationStats.averageGenerationTime.toFixed(2)
      }
    };
    
    // Pool-spezifische Statistiken
    for (const [type, pool] of this.pools.entries()) {
      const available = pool.filter(item => !item.used).length;
      const used = pool.filter(item => item.used).length;
      
      stats.pools[type] = {
        total: pool.length,
        available,
        used,
        utilizationRate: pool.length > 0 ? (used / pool.length * 100).toFixed(2) : 0
      };
    }
    
    return stats;
  }

  /**
   * Bereinigt ungenutzte IDs aus den Pools
   * @param {number} maxAge - Maximale Alter in Millisekunden
   */
  cleanupPools(maxAge = 24 * 60 * 60 * 1000) { // 24 Stunden
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [type, pool] of this.pools.entries()) {
      const originalLength = pool.length;
      
      // Entferne ungenutzte IDs, die älter als maxAge sind
      const cleanedPool = pool.filter(item => {
        if (!item.used && (now - item.generatedAt) > maxAge) {
          cleanedCount++;
          return false;
        }
        return true;
      });
      
      this.pools.set(type, cleanedPool);
      
      if (cleanedCount > 0) {
        console.log(`🧹 IDPoolManager: Cleaned ${cleanedCount} old IDs from ${type} pool`);
      }
    }
  }

  /**
   * Setzt alle Pools zurück
   */
  resetPools() {
    this.pools.clear();
    this.generationStats = {
      totalGenerated: 0,
      poolHits: 0,
      poolMisses: 0,
      averagePoolTime: 0,
      averageGenerationTime: 0
    };
    this.initializePools();
    console.log('🔄 IDPoolManager: All pools reset');
  }

  /**
   * Optimiert Pool-Größen basierend auf Nutzungsstatistiken
   */
  optimizePools() {
    for (const [type, pool] of this.pools.entries()) {
      const stats = this.getPoolStats().pools[type];
      const utilizationRate = parseFloat(stats.utilizationRate);
      
      if (utilizationRate > 80) {
        // Hohe Nutzung, Pool vergrößern
        this.poolSize = Math.min(this.maxPoolSize, this.poolSize + 50);
        console.log(`📈 IDPoolManager: Increased pool size for ${type} to ${this.poolSize}`);
      } else if (utilizationRate < 20 && this.poolSize > 50) {
        // Niedrige Nutzung, Pool verkleinern
        this.poolSize = Math.max(50, this.poolSize - 25);
        console.log(`📉 IDPoolManager: Decreased pool size for ${type} to ${this.poolSize}`);
      }
    }
  }
}

// Singleton-Instanz erstellen
const idPoolManager = new IDPoolManager();

export default idPoolManager;
